import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

let scriptLoading = null;
function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Razorpay script failed to load"));
    document.body.appendChild(s);
  });
  return scriptLoading;
}

/**
 * Initiates a Razorpay payment for a job posting.
 * Pass couponCode (and city) so the backend applies the discount and
 * returns the reduced amount — Razorpay then shows the discounted total.
 * Returns { payment_id, dummy_mode } on success, throws on failure/cancel.
 */
export async function payForJobPost({ category, city = "", couponCode = "", prefill = {} }) {
  // 1. Create order on backend — coupon is forwarded and applied server-side
  const { data: order } = await api.post("/payments/create-order", {
    category,
    city,
    coupon_code: couponCode,
  });

  // 2. Coupon covers the full amount → backend already recorded a paid ₹0 payment, no gateway
  if (order.is_free) {
    toast.success("Coupon applied — full amount covered. Job post unlocked.");
    return { payment_id: order.payment_id, dummy_mode: true, free: true };
  }

  // 3. Dummy mode (no real keys) → simulate verify directly
  if (order.dummy_mode) {
    const fakePaymentId = "pay_dummy_" + Math.random().toString(36).slice(2, 14);
    const { data: vres } = await api.post("/payments/verify", {
      razorpay_order_id: order.order_id,
      razorpay_payment_id: fakePaymentId,
      razorpay_signature: "",
    });
    toast.message("Payment simulated (dummy keys). Job post unlocked.");
    return { payment_id: vres.payment_id, dummy_mode: true };
  }

  // 4. Real Razorpay checkout — order.amount is the DISCOUNTED amount (paise)
  await loadRazorpay();
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Job Dew",
      description: `Job posting fee (${category})`,
      order_id: order.order_id,
      prefill,
      theme: { color: "#1D6FF2" },
      handler: async (resp) => {
        try {
          const { data: vres } = await api.post("/payments/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success("Payment successful!");
          resolve({ payment_id: vres.payment_id, dummy_mode: false });
        } catch (e) {
          toast.error(formatError(e));
          reject(e);
        }
      },
      modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
    });
    rzp.open();
  });
}